/**
 * JubileeTV chat widget.
 *
 * Wraps the support bot iframe in a launcher + panel. The iframe src is only
 * built the first time the panel opens, so the bot never costs anything on
 * pages where nobody starts a chat.
 *
 * Analytics: every widget action is pushed to the GTM dataLayer and to Shopify
 * custom pixels, following the pattern in survey.js. Only the widget's own
 * chrome is observable — the bot is a cross-origin iframe, so nothing about the
 * conversation itself (messages, intents, outcomes) can be tracked from here.
 * Those need the bot to postMessage out; see #onBotMessage for the entry point.
 */
(function () {
  const STATE_KEY = 'jtv-chat:state';
  const SESSION_KEY = 'jtv-chat:session';
  // Inactivity window for a conversation; refreshed on every read.
  const SESSION_TTL = 1000 * 60 * 60 * 24;
  // Bot -> parent events meaning "there is something new to read". Confirm the
  // real name with the bot team and trim this list down to it.
  const BOT_REPLY_EVENTS = ['bot_response_ready'];
  const MOBILE_QUERY = '(max-width: 749px)';
  const LOAD_TIMEOUT = 10000;

  class JtvChatWidget extends HTMLElement {
    #launcher;
    #panel;
    #frame;
    #loading;
    #mobile;
    #frameLoaded = false;
    #autoOpenTimer = null;
    #loadTimer = null;
    #frameRequestedAt = 0;
    #openedAt = 0;
    #openMethod = '';
    #sessionId = '';
    #badge;
    #unreadLabel;
    #unread = false;

    connectedCallback() {
      this.#launcher = this.querySelector('[data-chat-launcher]');
      this.#panel = this.querySelector('[data-chat-panel]');
      this.#frame = this.querySelector('[data-chat-frame]');
      this.#loading = this.querySelector('[data-chat-loading]');
      this.#badge = this.querySelector('[data-chat-badge]');
      this.#unreadLabel = this.querySelector('[data-chat-unread-label]');
      this.#mobile = window.matchMedia(MOBILE_QUERY);

      if (!this.#launcher || !this.#panel || !this.#frame) return;

      window.addEventListener('message', this.#onBotMessage);
      window.addEventListener('storage', this.#onStorage);
      this.#launcher.addEventListener('click', () => this.toggle());
      this.#panel.addEventListener('keydown', this.#onKeydown);
      this.#frame.addEventListener('load', this.#onFrameLoad);
      this.#mobile.addEventListener('change', this.#onBreakpointChange);

      this.querySelector('[data-chat-minimize]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-chat-close]')?.addEventListener('click', () => this.close(true));

      this.#syncPanelInert();

      if (this.dataset.trackImpressions === 'true') this.#track('impression');

      this.#restoreUnread();
      this.#restoreState();
      this.#bindThemeEditor();
    }

    /** In the theme editor, selecting the section previews the open panel. */
    #bindThemeEditor() {
      if (this.dataset.designMode !== 'true') return;

      document.addEventListener('shopify:section:select', (event) => {
        if (event.target.contains(this)) this.open({ focus: false, method: 'editor' });
      });

      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target.contains(this)) this.close();
      });
    }

    disconnectedCallback() {
      clearTimeout(this.#autoOpenTimer);
      clearTimeout(this.#loadTimer);
      this.#mobile?.removeEventListener('change', this.#onBreakpointChange);
      window.removeEventListener('message', this.#onBotMessage);
      window.removeEventListener('storage', this.#onStorage);
      this.#unlockScroll();
    }

    get isOpen() {
      return this.dataset.state === 'open';
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    /**
     * `focus` is false when restoring across a page load, so the panel does not
     * snatch focus from the page the visitor just landed on. `method` records
     * what opened it, and rides along on the close event too, so an auto-opened
     * panel that gets dismissed straight away is distinguishable in reports.
     */
    open({ focus = true, method = 'launcher' } = {}) {
      if (this.isOpen) return;

      clearTimeout(this.#autoOpenTimer);
      const hadUnread = this.#unread;
      this.#openMethod = method;
      this.#openedAt = Date.now();

      this.#loadFrame();
      this.#setUnread(false);
      this.dataset.state = 'open';
      this.#syncPanelInert();
      this.#storeState('open');

      if (this.#mobile.matches) this.#lockScroll();

      if (focus) {
        // Wait for the panel to become visible before moving focus into it.
        requestAnimationFrame(() => {
          this.querySelector('[data-chat-minimize], [data-chat-close]')?.focus({ preventScroll: true });
        });
      }

      this.#track('open', { method, had_unread: hadUnread });
    }

    /**
     * Collapses the panel back to the launcher. `reset` ends the conversation
     * — the bot reloads fresh the next time the panel is opened — which is what
     * the close button does; minimize keeps the thread alive on this page.
     */
    close(reset = false) {
      if (!this.isOpen) return;

      clearTimeout(this.#autoOpenTimer);
      this.dataset.state = 'closed';
      this.#syncPanelInert();
      this.#storeState('dismissed');
      this.#unlockScroll();
      this.#launcher.focus({ preventScroll: true });

      this.#track(reset ? 'close' : 'minimize', {
        engagement_ms: this.#openedAt ? Date.now() - this.#openedAt : 0,
        open_method: this.#openMethod,
      });

      this.#openedAt = 0;

      if (reset) {
        this.#resetFrame();
        this.#endSession();
      }
    }

    /* ----------------------------------------------------------------- unread */

    /**
     * The dot on the launcher. Persisted with the conversation so a reply that
     * arrived while the panel was minimised is still flagged after the visitor
     * navigates to another page — the iframe is not loaded until they open it,
     * so the bot gets no second chance to announce itself.
     * @param {boolean} unread
     */
    #setUnread(unread, { track = true } = {}) {
      if (this.#unread === unread) return;

      this.#unread = unread;
      this.dataset.unread = String(unread);
      console.log('CHATBOT:::', this.#unreadLabel, unread)
      if (this.#unreadLabel) this.#unreadLabel.hidden = !unread;

      this.#storeSession();

      if (unread && track) this.#track('unread');
    }

    #restoreUnread() {
      if (this.#readSession()?.unread) this.#setUnread(true, { track: false });
    }

    /* ------------------------------------------------------------- analytics */

    /**
     * Fires a widget event onto the DOM, the GTM dataLayer and Shopify custom
     * pixels — the same three channels survey.js uses.
     */
    #track(name, payload = {}) {
      console.log('CHATBOT::: track payload', payload);
      // Theme editor activity is the merchant, not a visitor.
      if (this.dataset.designMode === 'true') return;

      const prefix = this.#eventPrefix();
      const detail = {
        page_path: window.location.pathname,
        page_type: this.dataset.pageType || '',
        ...payload,
      };

      if (this.#sessionId) detail.chat_session_id = this.#sessionId;

      this.dispatchEvent(new CustomEvent(`${prefix}:${name}`, { detail, bubbles: true }));

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `${prefix}_${name}`, ...detail });
      console.log('CHATBOT::: event', detail);
      if (window.Shopify?.analytics?.publish) {
        try {
          window.Shopify.analytics.publish(`${prefix}_${name}`, detail);
        } catch {
          // Custom pixel not listening — nothing to do.
        }
      }
    }

    /** Keeps the merchant-set prefix within GA4's naming rules. */
    #eventPrefix() {
      const cleaned = (this.dataset.eventPrefix || '')
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^[^a-z]+/, '')
        .replace(/_+$/, '');

      return cleaned || 'chatbot';
    }

    /**
     * One id per conversation, not per visitor, and the bot owns it: it posts
     * SESSION_ID after every load and whatever it sends wins. If the id we
     * passed was stale and the bot opened a fresh conversation instead,
     * adopting its new id is what stops us pinning a dead chat_id to every
     * later page load.
     *
     * Kept in localStorage rather than sessionStorage: sessionStorage is scoped
     * to one tab by spec, so a new tab would start with no id, send no chat_id,
     * and get a brand new conversation with no history.
     * @param {string} sessionId
     */
    #adoptSession(sessionId) {
      if (!sessionId || sessionId === this.#sessionId) return;

      this.#sessionId = sessionId;
      this.#storeSession(sessionId);
    }

    #storeSession(id = this.#sessionId, unread = this.#unread) {
      if (!id) return;

      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now(), unread }));
      } catch {
        /* Private browsing — the id still holds for this page. */
      }
    }

    #endSession() {
      this.#sessionId = '';
      this.#setUnread(false, { track: false });

      try {
        localStorage.removeItem(SESSION_KEY);
      } catch {
        /* Nothing stored to clear. */
      }
    }

    /** Keeps tabs in step: one ending the chat, or reading it, clears the dot here. */
    #onStorage = (event) => {
      if (event.key !== SESSION_KEY) return;

      if (!event.newValue) {
        this.#sessionId = '';
        this.#setUnread(false, { track: false });
        return;
      }

      try {
        const parsed = JSON.parse(event.newValue);
        if (parsed && typeof parsed === 'object') {
          this.#setUnread(Boolean(parsed.unread), { track: false });
        }
      } catch {
        /* Not our format — leave the dot alone. */
      }
    };

    /* ----------------------------------------------------------------- frame */

    /** Builds the bot URL from the base, the current page params and the token. */
    #loadFrame() {
      if (this.#frameLoaded) return;

      const baseUrl = this.#frame.dataset.baseUrl;
      if (!baseUrl) return;

      let url;
      try {
        url = new URL(baseUrl, window.location.origin);
      } catch {
        return;
      }

      new URLSearchParams(window.location.search).forEach((value, key) => {
        url.searchParams.set(key, value);
      });

      const token = this.#frame.dataset.token;
      if (token) url.searchParams.set('token', token);

      const session = this.#readSession({ touch: true });
      if (session) {
        this.#sessionId = session.id;
        url.searchParams.set('chat_id', session.id);
      }
      console.log('CHATBOT:::','loadFrame ', url.toString());
      this.#frame.src = url.toString();
      this.#frameLoaded = true;
      this.#frameRequestedAt = Date.now();

      // Catches a stalled load only: browsers still fire `load` for a frame the
      // bot refuses to be embedded in, so this will not report an X-Frame-Options
      // or frame-ancestors block.
      clearTimeout(this.#loadTimer);
      this.#loadTimer = setTimeout(() => {
        this.#loadTimer = null;
        this.#track('load_error', { reason: 'timeout', timeout_ms: LOAD_TIMEOUT });
      }, LOAD_TIMEOUT);
    }

    /** Drops the bot session so the next open starts a new conversation. */
    #resetFrame() {
      clearTimeout(this.#loadTimer);
      this.#loadTimer = null;
      this.#frameRequestedAt = 0;
      this.#frameLoaded = false;
      this.#frame.removeAttribute('src');
      if (this.#loading) delete this.#loading.dataset.hidden;
    }

    #onFrameLoad = () => {
      if (!this.#frame.getAttribute('src')) return;

      if (this.#loading) this.#loading.dataset.hidden = 'true';

      clearTimeout(this.#loadTimer);
      this.#loadTimer = null;

      console.log('CHATBOT:::','onFrameLoad postMessage', this.#frame.dataset.baseUrl);
      this.#frame.contentWindow?.postMessage(
        {type: 'GET_SESSION_ID'},
        this.#frame.dataset.baseUrl
      );

      if (this.#frameRequestedAt) {
        this.#track('ready', { load_ms: Date.now() - this.#frameRequestedAt });
        this.#frameRequestedAt = 0;
      }
    };

    /* --------------------------------------------------------------- plumbing */

    #onKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close();
      }
    };

    #onBotMessage = (/** @type {{ origin: string; data: {type:string, event:string, sessionId:string}; }} */ event) => {
      
      const baseUrl = this.#frame?.dataset.baseUrl;
      if (!baseUrl) return;
      
      let botOrigin;
      try {
        botOrigin = new URL(baseUrl, window.location.origin).origin;
      } catch {
        return;
      }

      if (event.origin !== botOrigin) return;
      console.log('CHATBOT:::','onBotMessage', event);

      const msg = event.data || {};

      if (BOT_REPLY_EVENTS.includes(msg.event)) {
        // No payload spread: a reply event may carry the message body, which
        // must not reach GA4. Add named fields once the bot's shape is known.
        this.#track(msg.event);
        console.log('CHATBOT:::', msg.event, this.isOpen)
        if (!this.isOpen) this.#setUnread(true);
        return;
      }

      if (msg.type === 'SESSION_ID') {               
        this.#adoptSession(msg.sessionId);
        return;
      }

      switch (msg.event) {
        case 'message_sent':
        case 'thumbs_up':
        case 'thumbs_down':
        case 'ticket_raised':
        case 'link_clicked':
          this.#track(msg.event, msg);    
          break;
        default:
          break;                                     
      }
    };

    #onBreakpointChange = (event) => {
      if (this.isOpen && event.matches) {
        this.#lockScroll();
      } else {
        this.#unlockScroll();
      }
    };

    #syncPanelInert() {
      this.#panel.inert = !this.isOpen;
      this.#launcher.setAttribute('aria-expanded', String(this.isOpen));
    }

    #lockScroll() {
      document.body.classList.add('jtv-chat-scroll-locked');
    }

    #unlockScroll() {
      document.body.classList.remove('jtv-chat-scroll-locked');
    }

    /**
     * The stored conversation record, or null once it has gone stale. `touch`
     * re-stamps it, so SESSION_TTL measures inactivity in the chat rather than
     * the age of the conversation — a plain page view must not extend it.
     */
    #readSession({ touch = false } = {}) {
      let raw;
      try {
        raw = localStorage.getItem(SESSION_KEY);
      } catch {
        return null;
      }

      if (!raw) return null;

      let id = raw;
      let ts = 0;
      let unread = false;

      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          id = parsed.id;
          ts = Number(parsed.ts) || 0;
          unread = Boolean(parsed.unread);
        }
      } catch {
        /* A bare id string from before the stamp existed — treat it as fresh. */
      }

      if (!id) return null;

      if (ts && Date.now() - ts > SESSION_TTL) {
        this.#endSession();
        return null;
      }

      if (touch) this.#storeSession(id, unread);

      return { id, unread };
    }

    #storeState(value) {
      try {
        sessionStorage.setItem(STATE_KEY, value);
      } catch {
        /* Private browsing — the widget just forgets between pages. */
      }
    }

    #readState() {
      try {
        return sessionStorage.getItem(STATE_KEY);
      } catch {
        return null;
      }
    }

    /**
     * Keeps a chat open across page navigation, and otherwise honours the
     * auto-open delay unless the visitor already dismissed the panel.
     */
    #restoreState() {
      if (this.dataset.designMode === 'true') return;

      const stored = this.#readState();
      if (stored === 'open') {
        this.open({ focus: false, method: 'restored' });
        return;
      }

      const delay = Number(this.dataset.autoOpenDelay || 0);
      if (delay > 0 && stored !== 'dismissed') {
        this.#autoOpenTimer = setTimeout(() => this.open({ focus: false, method: 'auto' }), delay * 1000);
      }
    }
  }

  if (!customElements.get('jtv-chat-widget')) {
    customElements.define('jtv-chat-widget', JtvChatWidget);
  }
})();
